import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Container,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { BRAND_GRADIENTS } from '../config/brand';
import { authApi } from '../config/apiClient';

/**
 * Şifre sıfırlama sayfası — forgot-password e-postasındaki
 * `/reset-password?token=...` bağlantısı buraya gelir. Own-auth
 * `POST /api/auth/reset-password` ucunu çağırır.
 */
const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get('token') ?? '', [searchParams]);

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Geçersiz veya eksik sıfırlama bağlantısı.');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      setError('Yeni şifre en az 6 karakter olmalıdır.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Şifreler eşleşmiyor.');
      return;
    }

    setLoading(true);
    try {
      await authApi.post('/api/auth/reset-password', { token, newPassword });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 400) {
        setError('Bağlantı geçersiz veya süresi dolmuş. Lütfen yeniden talep edin.');
      } else if (!err?.response) {
        setError('Ağ hatası. Lütfen tekrar deneyin.');
      } else {
        setError('Şifre sıfırlanamadı. Lütfen tekrar deneyin.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: BRAND_GRADIENTS?.primary ?? 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
        p: 2,
      }}
    >
      <Container maxWidth="xs" disableGutters>
        <Paper elevation={6} sx={{ p: 4, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
            <LockResetIcon color="primary" sx={{ fontSize: 48 }} />
            <Typography variant="h6" sx={{ mt: 1, fontWeight: 600 }}>
              Şifre Sıfırlama
            </Typography>
          </Box>

          {success ? (
            <Alert severity="success">
              Şifreniz başarıyla güncellendi. Giriş sayfasına yönlendiriliyorsunuz...
            </Alert>
          ) : (
            <form onSubmit={handleSubmit} noValidate>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Lütfen yeni şifrenizi belirleyin.
              </Typography>

              <TextField
                label="Yeni Şifre"
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                size="small"
                autoComplete="new-password"
                disabled={loading}
                sx={{ mb: 2 }}
                helperText="En az 6 karakter"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowNew((s) => !s)} edge="end" size="small" tabIndex={-1}>
                        {showNew ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Yeni Şifre (Tekrar)"
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                size="small"
                autoComplete="new-password"
                disabled={loading}
                error={confirmPassword.length > 0 && confirmPassword !== newPassword}
                helperText={
                  confirmPassword.length > 0 && confirmPassword !== newPassword ? 'Şifreler eşleşmiyor' : ' '
                }
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirm((s) => !s)} edge="end" size="small" tabIndex={-1}>
                        {showConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              {error && (
                <Alert severity="error" sx={{ mt: 1, mb: 1 }}>
                  {error}
                </Alert>
              )}

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={loading}
                sx={{ mt: 2 }}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <LockResetIcon />}
              >
                {loading ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
              </Button>

              <Button onClick={() => navigate('/login')} fullWidth sx={{ mt: 1 }} color="inherit" disabled={loading}>
                Giriş sayfasına dön
              </Button>
            </form>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPassword;
